import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from './firebase';
import { AlertCircle, Users, ChevronDown, ChevronUp, Trash2, Link as LinkIcon } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Badge } from './components/ui/badge';

export default function DuplicateMerger() {
  const [duplicates, setDuplicates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedPair, setExpandedPair] = useState(null);
  const [mergeChoices, setMergeChoices] = useState({});

  // Find duplicates on load
  useEffect(() => {
    findDuplicates();
  }, []);

  const findDuplicates = async () => {
    setIsLoading(true);
    const readersRef = collection(db, 'readers');
    const snapshot = await getDocs(readersRef);

    const readersByEmail = {};
    snapshot.forEach(doc => {
      const data = doc.data();
      const email = data.emailCorrespondence?.toLowerCase();
      if (email) {
        if (!readersByEmail[email]) {
          readersByEmail[email] = [];
        }
        readersByEmail[email].push({ id: doc.id, ...data });
      }
    });

    // Find emails with multiple records
    const dupes = Object.entries(readersByEmail)
      .filter(([email, readers]) => readers.length > 1)
      .map(([email, readers]) => ({ email, readers }));

    setDuplicates(dupes);
    setIsLoading(false);
  };

  const toggleExpanded = (email) => {
    if (expandedPair === email) {
      setExpandedPair(null);
      setMergeChoices({});
    } else {
      setExpandedPair(email);
      // Initialize merge choices (default to reader 0 for all fields)
      const fields = getAllFields(duplicates.find(d => d.email === email).readers);
      const initialChoices = {};
      fields.forEach(field => {
        initialChoices[field] = 0; // Default to first reader
      });
      setMergeChoices(initialChoices);
    }
  };

  const getAllFields = (readers) => {
    const fieldSet = new Set();
    readers.forEach(reader => {
      Object.keys(reader).forEach(key => {
        if (key !== 'id') fieldSet.add(key);
      });
    });
    return Array.from(fieldSet).sort();
  };

  const getFieldValue = (reader, field) => {
    const value = reader[field];
    if (value === undefined || value === null || value === '') return '—';
    if (typeof value === 'object' && value.toDate) {
      return value.toDate().toLocaleDateString();
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const handleFieldChoice = (field, readerIndex) => {
    setMergeChoices(prev => ({
      ...prev,
      [field]: readerIndex
    }));
  };

  const performMerge = async (email) => {
    const dupeGroup = duplicates.find(d => d.email === email);
    if (!dupeGroup) return;

    const { readers } = dupeGroup;
    const fields = getAllFields(readers);

    // Build merged data object
    const mergedData = { emailCorrespondence: email };
    fields.forEach(field => {
      const chosenReaderIndex = mergeChoices[field] || 0;
      const value = readers[chosenReaderIndex][field];
      if (value !== undefined && value !== null && value !== '') {
        mergedData[field] = value;
      }
    });

    try {
      // Update the first reader with merged data
      const keepId = readers[0].id;
      await updateDoc(doc(db, 'readers', keepId), {
        ...mergedData,
        mergedAt: new Date().toISOString(),
        mergedFrom: readers.slice(1).map(r => r.id)
      });

      // Delete the duplicate records
      const batch = writeBatch(db);
      readers.slice(1).forEach(reader => {
        batch.delete(doc(db, 'readers', reader.id));
      });
      await batch.commit();

      alert(`Merged ${readers.length} records for ${email}!`);

      // Refresh duplicates list
      findDuplicates();
      setExpandedPair(null);
      setMergeChoices({});
    } catch (error) {
      alert('Error merging records: ' + error.message);
    }
  };

  const deleteDuplicate = async (email, readerId) => {
    if (!window.confirm('Are you sure you want to delete this record? This cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'readers', readerId));
      alert('Record deleted!');
      findDuplicates();
    } catch (error) {
      alert('Error deleting record: ' + error.message);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">Scanning for duplicates...</p>
      </Card>
    );
  }

  if (duplicates.length === 0) {
    return (
      <Card className="border-green-100 bg-green-50 p-8 text-center">
        <Users className="mx-auto mb-3 h-12 w-12 text-green-600" />
        <p className="text-lg font-semibold text-green-900">No duplicates found!</p>
        <p className="mt-1 text-sm text-green-700">All reader emails are unique.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-slate-200 bg-slate-50 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-6 w-6 text-teal-600" />
          <div>
            <h3 className="font-bold text-slate-900">
              {duplicates.length} Duplicate Email{duplicates.length !== 1 ? 's' : ''} Found
            </h3>
            <p className="mt-1 text-sm text-teal-700">
              These readers have multiple records with the same email. Merge them to keep your data clean!
            </p>
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        {duplicates.map(({ email, readers }) => (
          <Card key={email} className="overflow-hidden">
            {/* Header */}
            <button
              onClick={() => toggleExpanded(email)}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div className="text-left">
                  <p className="font-semibold text-slate-900">{email}</p>
                  <p className="text-sm text-muted-foreground">
                    {readers.length} records - {readers.map(r => r.name || 'Unnamed').join(' & ')}
                  </p>
                </div>
              </div>
              {expandedPair === email ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </button>

            {/* Expanded Merge Interface */}
            {expandedPair === email && (
              <div className="border-t border-border p-6 bg-muted/30">
                <h4 className="mb-4 text-lg font-bold text-slate-900">
                  Choose which data to keep for each field:
                </h4>

                {/* Field-by-field comparison */}
                <div className="space-y-2 mb-6 max-h-96 overflow-y-auto">
                  {getAllFields(readers).map(field => {
                    const hasConflict = new Set(readers.map(r => getFieldValue(r, field))).size > 1;

                    return (
                      <div
                        key={field}
                        className={`rounded-lg border p-3 ${
                          hasConflict ? 'border-slate-200 bg-slate-50' : 'border-border bg-background'
                        }`}
                      >
                        <p className="mb-2 text-sm font-semibold text-slate-700">
                          {field}
                          {hasConflict && (
                            <Badge variant="secondary" className="ml-2 bg-slate-200 text-slate-800">
                              Conflict
                            </Badge>
                          )}
                        </p>
                        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${readers.length}, 1fr)` }}>
                          {readers.map((reader, idx) => (
                            <label
                              key={idx}
                              className={`flex cursor-pointer items-center gap-2 rounded-lg border-2 p-2 text-sm transition-all ${
                                mergeChoices[field] === idx
                                  ? 'border-slate-500 bg-slate-50'
                                  : 'border-border bg-background hover:border-teal-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`field-${field}`}
                                checked={mergeChoices[field] === idx}
                                onChange={() => handleFieldChoice(field, idx)}
                                className="h-4 w-4 text-teal-600"
                              />
                              <div className="flex-1 overflow-hidden">
                                <p className="text-xs text-muted-foreground">Record {idx + 1}</p>
                                <p className="truncate text-slate-900">{getFieldValue(reader, field)}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button onClick={() => performMerge(email)} className="flex-1">
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Merge Records
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setExpandedPair(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>

                {/* Delete Individual Records */}
                <div className="mt-4 border-t border-border pt-4">
                  <p className="mb-2 text-sm font-semibold text-muted-foreground">Or delete individual records:</p>
                  <div className="flex gap-2">
                    {readers.map((reader, idx) => (
                      <Button
                        key={reader.id}
                        variant="outline"
                        size="sm"
                        onClick={() => deleteDuplicate(email, reader.id)}
                        className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Record {idx + 1}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
